import { router } from "@inertiajs/react"
import Gleap from "gleap"
import { Languages } from "lucide-react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { usersPreferencePath } from "@/routes"

const AUTO_VALUE = "auto"

// Native name of a language ("de" → "Deutsch"), so the list of offered
// languages lives only in User::WIDGET_LANGUAGES on the backend.
function languageLabel(code: string) {
  try {
    const label = new Intl.DisplayNames([code], { type: "language" }).of(code)
    if (!label || label === code) {
      return code.toUpperCase()
    }

    return label.charAt(0).toLocaleUpperCase(code) + label.slice(1)
  } catch {
    return code.toUpperCase()
  }
}

interface GleapLanguageToggleProps {
  languages: string[]
  preferredLanguage: string | null
}

export default function GleapLanguageToggle({
  languages,
  preferredLanguage,
}: GleapLanguageToggleProps) {
  const [widgetOpen, setWidgetOpen] = useState(false)

  useEffect(() => {
    Gleap.on("open", () => setWidgetOpen(true))
    Gleap.on("close", () => setWidgetOpen(false))
  }, [])

  if (widgetOpen) {
    return null
  }

  const selectLanguage = (value: string) => {
    router.patch(
      usersPreferencePath(),
      { widget_preferred_language: value === AUTO_VALUE ? null : value },
      { preserveScroll: true, preserveState: true },
    )
  }

  return (
    <div className="fixed right-6 bottom-24 z-40">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            aria-label="Assistant language"
            className="bg-card size-9 rounded-full shadow-md"
          >
            <Languages className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top">
          <DropdownMenuLabel>Assistant language</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup
            value={preferredLanguage ?? AUTO_VALUE}
            onValueChange={selectLanguage}
          >
            <DropdownMenuRadioItem value={AUTO_VALUE}>
              Auto (browser language)
            </DropdownMenuRadioItem>
            {languages.map((code) => (
              <DropdownMenuRadioItem key={code} value={code}>
                {languageLabel(code)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
